// Sugar Alarm — cycle 434: the reading-age bound is derived from the feed
// it is watching, not from one constant that fits one feed.
//
// ⚠ WHY THIS FILE EXISTS. Cycle 433 set a single 6-minute bound, reasoned
// from "a CGM samples every 5 minutes". In the first five hours of v666
// `reading_age_reopen` fired 33 times and 25 of them came from ONE kiosk:
// the household it watches averages 6.8-minute gaps, so most ordinary
// intervals crossed a 6-minute bound and that kiosk reopened and reseeded
// roughly every six minutes, indefinitely. Nothing broke — the reseed
// landed every time, the proof of currency renewed, the alarm rails never
// saw it — but a device that reconnects forever because its feed is slower
// than one hardcoded number is being judged against somebody else's CGM.
//
// ⚠ THE BOUND MAY ONLY WIDEN, NEVER TIGHTEN. The floor IS cycle 433's
// constant, so no device can come out of this cycle quicker to stop
// trusting its own copy than it was before. A fast feed keeps exactly the
// bound it has today; only a slow feed is given the extra room it was
// always owed. That direction is deliberate: widening costs churn,
// tightening costs a blanked number, and only one of those is a safety
// question.
//
// ⚠ THIS DECIDES ONLY WHEN THE CLIENT STOPS TRUSTING ITS OWN COPY. The
// SERVER decides what is stale for alarm purposes — `glucose_service`'s
// provider-aware thresholds, mirrored for display by `lib/glucose.ts`'s
// `isStale`. Nothing here gates a takeover, a banner, a control or a
// sound, and nothing here is read by a rail predicate; the sweep in
// `__tests__/readingUnknown.test.ts` covers this module for the same
// reason it covers `readingFreshness.ts`.

import {
  READING_CONFIRM_BOUND_MS,
  RESEED_TIMEOUT_MS,
} from "./readingFreshness";

/** The lowest bound any feed may be given: cycle 433's constant.
 *
 *  It doubles as the "above a normal cadence for the fastest real feed"
 *  floor the cycle asked for. The fastest real feed on this platform is
 *  LibreLinkUp, whose merged live reading updates on roughly a one-minute
 *  cadence (the cycle 234.5 fix), and six minutes clears that by a wide
 *  margin. Deriving a 1.5-minute bound for a one-minute feed would be
 *  arithmetically faithful and operationally useless: a single hiccup on a
 *  fast feed would blank the number. The floor is what stops that. */
export const READING_BOUND_FLOOR_MS = READING_CONFIRM_BOUND_MS;

/** The highest bound any feed may be given.
 *
 *  ⚠ IT MUST STAY BELOW THE SERVER'S PROVIDER STALENESS RULE, which is ten
 *  minutes for both Dexcom and Libre. Past that the server itself calls
 *  the reading stale, so a client still presenting it as current would be
 *  trusting a value the platform has already given up on. Nine minutes
 *  leaves a minute of margin, and — because the number is withdrawn at
 *  `bound + RESEED_TIMEOUT_MS` — the very latest a value can still be on
 *  screen as current is 9 min 10 s, which is still inside the server's
 *  ten. `assertCeilingUnderServerStaleness` pins that arithmetic. */
export const READING_BOUND_CEILING_MS = 9 * 60_000;

/** How much slack past the expected gap before the client goes and asks.
 *
 *  Half a cadence. One full cadence of slack would mean a feed had to miss
 *  two consecutive samples before the client checked, and a dropped sample
 *  is exactly the moment worth checking; no slack at all would trip on the
 *  ordinary jitter between two readings, which is the defect this cycle is
 *  fixing. Crossing the bound costs one reconnect and a reseed, and if the
 *  reseed lands carrying the SAME reading the client has proven its copy
 *  is current and the clock resets — so the cost of being slightly early
 *  is churn, not a blanked number. */
export const READING_BOUND_MULTIPLIER = 1.5;

/** How many inter-reading gaps must be observed before a cadence is
 *  believed. Five gaps is six readings: on a five-minute feed that is
 *  about half an hour of evidence, enough that one backfill or one
 *  reconnect burst cannot define a member's cadence. Below this the
 *  fallback constant applies, which is cycle 433's behaviour unchanged. */
export const READING_CADENCE_MIN_GAPS = 5;

/** How many of the most recent gaps the median is taken over. Ten keeps
 *  the estimate responsive to a feed that genuinely changes cadence (a
 *  sensor swap, a provider switch) while still being long enough that the
 *  median is not moved by one outlier. */
export const READING_CADENCE_WINDOW = 10;

/** Gaps at or below this are not believed as cadence. Two readings landing
 *  in the same second happen on a catch-up burst, a backfill, or the
 *  cycle-429 batch delivery; counting them would drag the median toward
 *  zero and hand a slow feed the floor it was already failing. */
const MIN_BELIEVABLE_GAP_MS = 30_000;

/** Inter-reading gaps, oldest first, from reading timestamps.
 *
 *  ⚠ Measured from the READINGS' own sample times, never from when this
 *  client happened to receive them: the question is how often the feed
 *  produces a value, and receipt times are contaminated by every reconnect
 *  and catch-up batch. Unsorted, duplicated and non-finite input is
 *  tolerated because it arrives from the wire. */
export function readingGapsMs(timestampsMs: readonly number[]): number[] {
  const clean = timestampsMs
    .filter((t) => Number.isFinite(t))
    .slice()
    .sort((a, b) => a - b);
  const gaps: number[] = [];
  for (let i = 1; i < clean.length; i += 1) {
    const prev = clean[i - 1];
    const cur = clean[i];
    if (prev == null || cur == null) continue;
    const gap = cur - prev;
    if (gap >= MIN_BELIEVABLE_GAP_MS) gaps.push(gap);
  }
  return gaps;
}

/** The median of the most recent `READING_CADENCE_WINDOW` gaps, or `null`
 *  when fewer than `READING_CADENCE_MIN_GAPS` have been observed.
 *
 *  ⚠ MEDIAN, NOT MEAN, AND THAT IS THE WHOLE POINT. A feed that goes dark
 *  for two hours and comes back contributes one 7,200,000 ms gap; a mean
 *  over ten gaps would let that single outage roughly double the member's
 *  apparent cadence and hand the client a ceiling-width bound for the rest
 *  of the session. A median ignores it. */
export function medianGapMs(timestampsMs: readonly number[]): number | null {
  const gaps = readingGapsMs(timestampsMs).slice(-READING_CADENCE_WINDOW);
  if (gaps.length < READING_CADENCE_MIN_GAPS) return null;
  const sorted = gaps.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const hi = sorted[mid];
  const lo = sorted[mid - 1];
  if (hi == null) return null;
  if (sorted.length % 2 === 1) return hi;
  return lo == null ? hi : Math.round((lo + hi) / 2);
}

/** The reading-age bound for a feed, in ms. Pure.
 *
 *  Falls back to cycle 433's constant until a cadence can be believed, so
 *  a cold start, a fresh member switch and a sparse feed all behave
 *  exactly as they did before this cycle. */
export function readingBoundMs(timestampsMs: readonly number[]): number {
  const median = medianGapMs(timestampsMs);
  if (median == null) return READING_CONFIRM_BOUND_MS;
  const derived = Math.round(median * READING_BOUND_MULTIPLIER);
  return Math.min(
    READING_BOUND_CEILING_MS,
    Math.max(READING_BOUND_FLOOR_MS, derived),
  );
}

/** Test seam: the latest instant a reading may still be presented as
 *  current, for any feed. Kept as a function rather than a constant so the
 *  relationship to the ceiling and the reseed timeout is stated in one
 *  place and cannot drift. */
export function latestPresentableAgeMs(): number {
  return READING_BOUND_CEILING_MS + RESEED_TIMEOUT_MS;
}
